import { motion } from "framer-motion";
import { Users, Mountain, Landmark, Compass, Crown, Plane, Package, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useSiteData, useSectionDesign } from "../../context/SiteData";
import { useBooking } from "../../context/BookingContext";

const ICONS = {
  users: Users,
  mountain: Mountain,
  landmark: Landmark,
  compass: Compass,
  crown: Crown,
  plane: Plane,
  package: Package,
};

export const Services = () => {
  const { content } = useSiteData();
  const sec = useSectionDesign("services");
  const { openBooking } = useBooking();
  const items = content.services_items || [];

  if (!items.length) return null;

  return (
    <section id="services" className="relative py-24 sm:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[var(--brand)]" data-testid="services-eyebrow">
            {content.services_eyebrow}
          </p>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0b132b]" data-testid="services-title">
            {sec.text("headline", content.services_title)}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            {sec.text("subtext", content.services_subtitle)}
          </p>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
          {items.map((s, i) => {
            const Icon = ICONS[s.icon] || Compass;
            const wide = i === items.length - 1 && items.length % 3 === 1;
            return (
              <Reveal key={i} delay={(i % 3) * 0.08} className={wide ? "sm:col-span-2 lg:col-span-3" : ""}>
                <motion.div
                  data-testid={`service-card-${i}`}
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className={`group h-full rounded-3xl bg-[#f8fafc] border border-slate-100 p-7 hover:bg-white hover:shadow-[0_24px_50px_-20px_rgba(225,29,72,0.25)] hover:border-[var(--brand)]/25 transition-colors duration-300 ${
                    wide ? "flex flex-col sm:flex-row sm:items-center gap-5" : ""
                  }`}
                >
                  <span className="w-12 h-12 shrink-0 rounded-2xl bg-[var(--brand)] flex items-center justify-center shadow-lg shadow-[var(--brand)]/30 transition-transform duration-300 group-hover:rotate-6">
                    <Icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                  </span>
                  <div className={wide ? "" : "mt-5"}>
                    <h3 className="font-display font-bold text-lg text-[#0b132b]">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.copy}</p>
                  </div>
                </motion.div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15} className="mt-10">
          <button
            data-testid="services-book-button"
            onClick={() => openBooking({ mode: "tour" })}
            className="group inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] hover:-translate-y-0.5"
          >
            Book Your Tour
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};
